import { createReducer, on } from "@ngrx/store";
import { AchActions, CheckActions, InternalTransferActions, MasterStatusViewActions, MasterTransactionsTableActions, TransactionFormActions, TransactionsTableActions, WireActions } from "../../create-request/actions";
import { CheckInquiryResponse } from "../../create-request/models/check/CheckInquiryResponse";
import { DistributionReason } from "../../create-request/models/internal-transfer/DistributionReason";
import { percentFormatter } from "../../shared/utilities/PercentFormatter";
import { ViewPageActions } from "../actions";
import { ViewDetails } from "../models/ViewDetails";
import { CustomerEauthStatuses } from "../../shared/utilities/Constants";
import { StandingAuthMasterStatusTableActions, StandingAuthMasterStatusViewActions } from "../../standing-authorizations/actions";

export const viewDetailsKey = 'viewDetails';

export interface State extends ViewDetails { }

export const initialViewDetailsState: State = {
  caseId: '',
  status: {
    code: '',
    description: ''
  },
  auditFields: {
    isUpdated: false,
    request: {
      date: '',
      time: '',
      user: '',
      source: ''
    },
    update: {
      date: '',
      time: '',
      user: '',
      source: ''
    }
  },
  frequency: '',
  type: '',
  isPendingAuthorization: false,
  transactionType: '',
  cashieringRequestId: '',
  cashieringRequestSecondId: '',
  subAccountId: '',
  isCancelable: false,
  isEditable: false,
  isIncoming: false,
  distributionReason: new DistributionReason,
  distributionReasonsList: [],
  taxWithholding: {
    fedTaxAmount: '',
    fedTaxPercent: '',
    stateTaxAmount: '',
    stateTaxPercent: '',
    distributionReason: ''
  },
  trustTaxCategoriesAndCodes: {},
  inquiryCallComplete: false,
  isThirdParty: false,
  isBundled: false,
  releasedBy: '',
  releasedDate: '',
  initiatedDate: ''
}

export const viewDetailsReducer = createReducer(
  initialViewDetailsState,
  on(
    AchActions.callInquiry,
    WireActions.callInquiry,
    InternalTransferActions.callInquiry,
    CheckActions.callInquiry,
    (state, response) => {
      return {
        ...state,
        type: response.viewType,
        transactionType: response.transactionType,
        inquiryCallComplete: false,
        isCancelable: response.payload.isCancelable ?? false,
        isEditable: response.payload.isEditable ?? false,
        status: {
          code: response.payload.statusCd ?? '',
          description: response.payload.statusCodeDescription ?? ''
        }
      };
    }
  ),
  on(InternalTransferActions.callInquiry, (state, { type, ...action }) => {
    return {
      ...state,
      cashieringRequestId: action.payload.cashieringRqstId,
      inquiryCallComplete: false
    }
  }),
  on(
    MasterStatusViewActions.callMsvInternalTransferInquiry,
    MasterStatusViewActions.callMsvCheckInquiry,
    MasterStatusViewActions.callMsvAchInquiry,
    MasterStatusViewActions.callMsvWireInquiry,
     (state, action) => {
    return {
      ...state,
      type: action.viewType,
      transactionType: action.transactionType,
      cashieringRequestId: action.payload.attributes.cashieringRqstId,
      cashieringRequestSecondId: action.payload.attributes.cashieringRqstSecondId,
      subAccountId: action.payload.attributes.accountNumber.toString(),
      inquiryCallComplete: false,
      isCancelable: false,
      isEditable: false,
      status: {
        code: '',
        description: action.payload.attributes.statusCodeDescription ?? ''
      }
    }
  }),
  on(
    StandingAuthMasterStatusViewActions.callSaMsvInternalTransferInquiry,
    StandingAuthMasterStatusViewActions.callSaMsvCheckInquiry,
    StandingAuthMasterStatusViewActions.callSaMsvAchInquiry,
    StandingAuthMasterStatusViewActions.callSaMsvWireInquiry,
     (state, action) => {
    return {
      ...state,
      type: action.viewType,
      transactionType: action.transactionType,
      cashieringRequestId: action.payload.cashieringRqstId,
      cashieringRequestSecondId: action.payload.cashieringRqstSecondId,
      subAccountId: action.payload.accountNumber.toString(),
      inquiryCallComplete: false,
      isCancelable: false,
      isEditable: false,
      status: {
        code: '',
        description: action.payload.statusCodeDescription ?? ''
      }
    }
  }),
  on(InternalTransferActions.inquiryCallSuccessful, (state, response) => {
    const { internalTransferInquiryResponse } = response;
    return {
      ...state,
      caseId: internalTransferInquiryResponse.eAuthStatus.caseId,
      status: internalTransferInquiryResponse.status,
      isPendingAuthorization: internalTransferInquiryResponse.isPendingAuthorization,
      eAuthStatus: internalTransferInquiryResponse.eAuthStatus,
      taxId: internalTransferInquiryResponse.taxId,
      frequency: internalTransferInquiryResponse.recurringFields.frequency.description,
      auditFields: {
        isUpdated: internalTransferInquiryResponse.auditFields.isUpdated,
        request: {
          date: internalTransferInquiryResponse.auditFields.request.date,
          time: internalTransferInquiryResponse.auditFields.request.time,
          user: internalTransferInquiryResponse.auditFields.request.user,
          source: internalTransferInquiryResponse.auditFields.request.source,
        },
        update: {
          date: internalTransferInquiryResponse.auditFields.update.date,
          time: internalTransferInquiryResponse.auditFields.update.time,
          user: internalTransferInquiryResponse.auditFields.update.user,
          source: internalTransferInquiryResponse.auditFields.update.source,
        }
      },
      // commenting the below out until CMT makes the inquiry response accurate. These will be mapped based on overview/the tables in the meantime
      // isCancelable: internalTransferInquiryResponse?.isCancelable,
      // isEditable: internalTransferInquiryResponse?.isEditable,
      trustTaxCategoriesAndCodes: internalTransferInquiryResponse?.taxCodeList,
      inquiryCallComplete: true,
      isEauthForEditFlow: internalTransferInquiryResponse?.eAuthStatus?.isEAuth,
      cashieringRequestId: internalTransferInquiryResponse.cashieringReqId,
      cashieringRequestSecondId: internalTransferInquiryResponse.cashieringReqSecondaryId,
      taxWithholding: {
        fedTaxPercent: percentFormatter(internalTransferInquiryResponse.iraFields.fedTaxAmount),
        fedTaxAmount: '',
        stateTaxPercent: percentFormatter(internalTransferInquiryResponse.iraFields.stateTaxAmount),
        stateTaxAmount: '',
        distributionReason: internalTransferInquiryResponse.iraFields.distributionReason.description,
        distributionReasonMap: internalTransferInquiryResponse.iraFields.distributionReasonMap,
        stateHasTaxes: internalTransferInquiryResponse.iraFields.stwProcessCode !== 'N' ? true : false,
        stateTypeIndicator: internalTransferInquiryResponse.iraFields.stateTypeIndicator,
        stateCode: internalTransferInquiryResponse.stateCd,
        defaultStateTax: internalTransferInquiryResponse.iraFields.stateTaxAmount
      },
      isThirdParty: internalTransferInquiryResponse.isThirdParty,
      isBundled: internalTransferInquiryResponse.isBundledTransaction,
      releasedBy: internalTransferInquiryResponse.releasedBy,
      releasedDate: internalTransferInquiryResponse.releasedDate,
      initiatedDate: internalTransferInquiryResponse.initiatedDate
    };
  }),
  on(InternalTransferActions.acknowledgeCallSuccessful, (state, { type, ...action }) => {
    const { response } = action;
    return {
      ...state,
      taxId: response.taxId,
    };
  }),
  on(WireActions.inquiryCallSuccessful, (state, response) => {
    const { wireInquiryResponse } = response;
    return {
      ...state,
      caseId: wireInquiryResponse.aceCaseId.trim(),
      isPendingAuthorization: wireInquiryResponse.clientAuthorizationNeeded || wireInquiryResponse.eauthInd,
      taxId: wireInquiryResponse.taxId,
      eAuthStatus: {
        isEAuth: wireInquiryResponse.eauthInd,
        caseId: wireInquiryResponse.aceCaseId.trim(),
        customerList: wireInquiryResponse.custAcctAuthorizationsList.map((customer) => {
          return {
            firstName: customer.firstName,
            lastName: customer.lastName,
            customerId: customer.customerId,
            emailId: customer.email,
            customerEauthStatus: customer.custEAStatus as CustomerEauthStatuses
          }
        })
      },
      status: {
        code: wireInquiryResponse.statusCode === 'VRFY' ? 'PENDING' : wireInquiryResponse.statusCode,
        description: wireInquiryResponse.statusDescription === 'Pending-Review' ? 'Pending' : wireInquiryResponse.statusDescription,
      },
      auditFields: {
        isUpdated: wireInquiryResponse.isLastModified,
        request: {
          date: wireInquiryResponse.requestedDate,
          time: wireInquiryResponse.requestedTime,
          user: wireInquiryResponse.initiatedBy,
          source: wireInquiryResponse.source,
        },
        update: {
          date: wireInquiryResponse.lastUpdatedDate,
          time: wireInquiryResponse.lastUpdatedTime,
          user: wireInquiryResponse.isLastModifiedBy
            ? wireInquiryResponse.lastUpdatedBy
            : wireInquiryResponse.sourceOfLastModification,
          source: wireInquiryResponse.sourceOfLastModification,
        },
      },
      cashieringRequestId: wireInquiryResponse?.cashieringRequestId.toString(),
      // commenting the below out until CMT makes the inquiry response accurate. These will be mapped based on overview/the tables in the meantime
      // isCancelable: wireInquiryResponse?.isCancelable,
      // isEditable: wireInquiryResponse?.isEditable,
      inquiryCallComplete: true,
      isBundled: wireInquiryResponse.bSLOATxn,
      releasedBy: wireInquiryResponse.releasedBy,
      releasedDate: wireInquiryResponse.releasedDate,
      initiatedDate: wireInquiryResponse.initiatedDate
    };
  }),
  on(WireActions.acknowledgeCallV3Successful, (state, response) => {
    return {
      ...state,
      taxId: response.fromDetails.taxIdSSN,
    };
  }),
  on(AchActions.inquiryCallSuccessful, (state, { achInquiryResponse }) => {
    return {
      ...state,
      status: {
        ...state.status,
        description: achInquiryResponse.status,
      },
      auditFields: {
        ...state.auditFields,
        isUpdated: achInquiryResponse.isLastModified,
        request: {
          ...state.auditFields.request,
          date: achInquiryResponse.requestedDate,
          user: achInquiryResponse.initiatedBy,
          source: achInquiryResponse.source,
        },
        update: {
          ...state.auditFields.update,
          date: achInquiryResponse.lastModifiedDate,
          time: achInquiryResponse.lastModifiedTime,
          user: !!achInquiryResponse.lastModifiedBy.trim()
            ? achInquiryResponse.lastModifiedBy
            : achInquiryResponse.lastModifiedSource,
          source: achInquiryResponse.lastModifiedSource,
        },
      },
      frequency: achInquiryResponse.frequency,
      // commenting the below out until CMT makes the inquiry response accurate. These will be mapped based on overview/the tables in the meantime
      // isCancelable: achInquiryResponse.isCancelable,
      // isEditable: achInquiryResponse.isEditable,
      isIncoming: achInquiryResponse.incoming,
      inquiryCallComplete: true,
      cashieringRequestId: achInquiryResponse.cashieringReqId,
      cashieringRequestSecondId: achInquiryResponse.cashieringReqSecondaryId,
      taxId: achInquiryResponse.taxId,
      releasedBy: achInquiryResponse.releasedBy,
      releasedDate: achInquiryResponse.releasedDate,
      initiatedDate: achInquiryResponse.initiatedDate
    };
  }),
  on(AchActions.acknowledgeCallSuccessful, (state, { response }) => {
    return {
      ...state,
      taxId: response.taxId,
    };
  }),
  on(CheckActions.incomingInquiryCallSuccessful, (state, { checkInquiryResponse }) => {
    const { cashieringCheckInputDetails } = checkInquiryResponse;
    return {
      ...state,
      isIncoming: true,
      auditFields: {
        ...state.auditFields,
        isUpdated: cashieringCheckInputDetails.isUpdated,
        request: {
          ...state.auditFields.request,
          date: checkInquiryResponse.depositDate,
          time: checkInquiryResponse.timeOfDeposit,
          source: checkInquiryResponse.sourceDescription
        },
        update: {
          ...state.auditFields.update,
          date: checkInquiryResponse.lastModifiedDate,
          time: checkInquiryResponse.lastModifiedDate,
          source: checkInquiryResponse.lastModifiedBy
        }
      },
      status: {
        ...state.status,
        description: checkInquiryResponse.statusDescription,
      },
      inquiryCallComplete: true,
      isCancelable: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.isCancelable,
      isEditable: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.isEditable,
      isBundled: checkInquiryResponse?.isBundledSloaUsage,
      releasedBy: checkInquiryResponse.releasedBy,
      releasedDate: checkInquiryResponse.releasedDate,
      initiatedDate: checkInquiryResponse.initiatedDate
    }
  }),
  on(CheckActions.inquiryCallSuccessful, (state, { checkInquiryResponse }) => {
    const { cashieringCheckInputDetails } = checkInquiryResponse;
    return {
      ...state,
      taxId: checkInquiryResponse.taxId,
      caseId: checkInquiryResponse?.caseId?.trim(),
      isPendingAuthorization: checkInquiryResponse.isPendingForCTWE || checkInquiryResponse.isPendingForLOA,
      eAuthStatus: {
        caseId: checkInquiryResponse?.caseId,
        customerList: checkInquiryResponse?.eAuthModel?.custAcctAuthorizationsList?.eAuthCustomerList?.map((customer) => {
          return {
            firstName: customer.firstName,
            lastName: customer.lastName,
            customerId: customer.customerId,
            emailId: customer.emailId,
            customerEauthStatus: customer.loginIdEnrollStatus as CustomerEauthStatuses
          }
        }),
        isEAuth: checkInquiryResponse?.eAuthModel?.eauthInd
      },
      status: {
        ...state.status,
        code: checkInquiryResponse.statusCode,
        description: checkInquiryResponse.statusDescription,
      },
      auditFields: {
        ...state.auditFields,
        isUpdated: cashieringCheckInputDetails.isUpdated,
        request: {
          ...state.auditFields.request,
          date: cashieringCheckInputDetails.requestDate,
          time: cashieringCheckInputDetails.requestTime,
          source: cashieringCheckInputDetails.requestLocationDesc,
          user: checkInquiryResponse.initiatedBy
        },
        update: {
          ...state.auditFields.update,
          date: cashieringCheckInputDetails.lastUpdatedDate,
          time: cashieringCheckInputDetails.lastUpdatedTime,
          source: cashieringCheckInputDetails.updateLocationDesc,
          user: checkInquiryResponse.updatedBy
        }
      },
      cashieringRequestId: checkInquiryResponse?.cashieringReqId.toString(),
      cashieringRequestSecondId: checkInquiryResponse?.cashieringReqSecondaryId.toString(),
      // commenting the below out until CMT makes the inquiry response accurate. These will be mapped based on overview/the tables in the meantime
      // isCancelable: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.isCancelable,
      // isEditable: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.isEditable,
      distributionReason: mapCheckDistributionReasonList(checkInquiryResponse),
      distributionReasonsList: checkInquiryResponse?.distributionReasonsList,
      taxWithholding: {
        fedTaxPercent: getFormattedTaxPercent(checkInquiryResponse?.fedTaxAmountFormatted),
        fedTaxAmount: '',
        stateTaxPercent: getFormattedTaxPercent(checkInquiryResponse?.stateTaxAmountFormatted),
        stateTaxAmount: '',
        distributionReason: checkInquiryResponse?.distributionReasonDescription,
        distributionReasonMap: checkInquiryResponse?.distributionReasonsList,
        stateHasTaxes: checkInquiryResponse?.stwProcessCode !== 'N' ? true : false,
        stateTypeIndicator: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.iraTxn?.stateTypeInd,
        stateCode: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.stateCd,
        defaultStateTax: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.stateTaxAmountFormatted
      },
      trustTaxCategoriesAndCodes: checkInquiryResponse?.taxCodeList,
      isEauthForEditFlow: checkInquiryResponse?.eAuthModel?.eauthInd,
      inquiryCallComplete: true,
      isBundled: checkInquiryResponse?.isBundledSloaUsage,
      releasedBy: checkInquiryResponse.releasedBy,
      releasedDate: checkInquiryResponse.releasedDate,
      initiatedDate: checkInquiryResponse.initiatedDate
    }
  }),
  on(CheckActions.acknowledgeCallSuccessful, (state, { type, ...action }) => {
    const { checkAcknowledgeResponse } = action;
    return {
      ...state,
      taxId: checkAcknowledgeResponse.taxId,
    };
  }),
  on(ViewPageActions.backButtonClicked, 
    ViewPageActions.backButtonClickedFromMsv, 
    ViewPageActions.startTransactionButtonClicked, 
    TransactionFormActions.cancelClicked, 
    () => {
    return {
      ...initialViewDetailsState,
    };
  }),
  on(ViewPageActions.resetIsEauthForEditFlow, (state) => {
    return {
      ...state,
      isEauthForEditFlow: undefined
    }
  }),
  on(TransactionsTableActions.loadViewPage,
    MasterTransactionsTableActions.loadViewPage,
    StandingAuthMasterStatusTableActions.loadViewPage, (state, payload) => {
    return {
      ...state,
      type: payload.viewType
    }
  })
);

function mapCheckDistributionReasonList(checkInquiryResponse: CheckInquiryResponse): DistributionReason {
  let distributionReasonCode = checkInquiryResponse.distributionReasonCode;
  let distributionReasonCodeSubcode = checkInquiryResponse.distributionReasonCodeSubcode?.split('-')[1];

  return checkInquiryResponse.distributionReasonsList?.find(reason => reason.code === distributionReasonCode && reason.subCode.trim() === distributionReasonCodeSubcode.trim()) ?? new DistributionReason();
}

function getFormattedTaxPercent(original: string): string {
  let newVal = original.split(' ')[1];
  if (newVal) {
    return newVal?.replace('(', '').replace(')', '');
  } else {
    return original;
  }
}
